/**
 * @file loader entry
 * @author cxtom (cxtom2008@gmail.com)
 */

/* eslint-disable fecs-no-require */

import path from 'path';
import qs from 'querystring';
import loaderUtils from 'loader-utils';
import parse from './parser';
import selectBlock from './select';
import {attrsToQuery} from './codegen/utils';
import {getBlocks} from 'atom-web-compiler';
import {NS} from './plugin';

let errorEmitted = false;

function loadAtomCompiler(loaderContext) {
    try {
        return require('atom-web-compiler');
    }
    catch (e) {
        loaderContext.emitError(new Error(
            '[atom-loader] atom-web-compiler must be installed as a peer dependency. '
        ));
    }
}

module.exports = function (content) {

    const loaderContext = this;

    if (!errorEmitted && !loaderContext['thread-loader'] && !loaderContext[NS]) {
        loaderContext.emitError(new Error(
            'atom-loader was used without the corresponding plugin. '
            + 'Make sure to include AtomLoaderPlugin in your webpack config.'
        ));
        errorEmitted = true;
    }

    const stringifyRequest = r => loaderUtils.stringifyRequest(loaderContext, r);

    const {
        sourceMap,
        rootContext,
        resourcePath,
        resourceQuery
    } = loaderContext;

    const rawQuery = resourceQuery.slice(1);
    const inheritQuery = `&${rawQuery}`;
    const incomingQuery = qs.parse(rawQuery);
    const query = loaderUtils.getOptions(this) || {};

    const options = Object.assign(
        {},
        (this.options || this._compiler.options).atom,
        this.atom,
        query
    );

    const filename = path.basename(resourcePath);
    const context = rootContext || process.cwd();
    const sourceRoot = path.dirname(path.relative(context, resourcePath));

    const blocks = getBlocks(content);

    // script
    let scriptRequest = '';
    if (blocks.script) {
        const src = blocks.script.src || resourcePath;
        const attrsQuery = attrsToQuery({lang: blocks.script.lang || 'js'}, 'js');
        const query = `?atom&type=script${attrsQuery}${inheritQuery}`;
        scriptRequest = stringifyRequest(src + query);
    }

    const compilerOptions = Object.assign({
        context
    }, options.compile, {
        filename,
        compileJsScript() {
            return {
                code: `
                module.exports = require(${scriptRequest});
                `
            };
        }
    });

    let result;

    try {
        result = parse({
            compilerOptions,
            source: content,
            compiler: options.compiler || loadAtomCompiler(loaderContext),
            filename,
            sourceRoot,
            needMap: sourceMap,
            loaderContext
        });
    }
    catch (e) {
        this.callback(e);
        return;
    }

    // if the query has a type field, this is a language block request
    // e.g. foo.atom?type=script&id=xxxxx
    // and we will return early
    if (incomingQuery.type && incomingQuery.atom !== null) {
        return selectBlock(
            result,
            loaderContext,
            incomingQuery
        );
    }

    let output = '';
    if (!options.excludeCSS && blocks.styles.length > 0) {
        const attrsQuery = attrsToQuery({}, 'css');
        // make sure to only pass id when necessary so that we don't inject
        // duplicate tags when multiple components import the same css file
        const query = `?atom&type=style${attrsQuery}${inheritQuery}`;
        output = `require(${stringifyRequest(resourcePath + query)});`;
    }
    output += result.compiled.js;

    if (typeof options.resolvePhpOutputPath === 'function' && !options.nodeSsrOnly) {
        let outputPath = options.resolvePhpOutputPath(resourcePath);
        if (!options.nodeSsrOnly) {
            loaderContext.emitFile(outputPath, result.compiled.php);
        }
    }

    if (options.ssrCompile && typeof options.resolveSsrOutputPath === 'function') {
        let outputPath = options.resolveSsrOutputPath(resourcePath);
        const {
            compiler: ssrCompiler,
            runtimePath,
            atomCompilerOptions,
            needCustomComponentPath
        } = options.ssrCompile;
        let ssrResult = ssrCompiler.compile(content, {
            rootContext: this.rootContext,
            resourcePath: this.resourcePath,
            runtimePath,
            inject: 'condition',
            atomCompilerOptions,
            staticScopedId: result.compiled.scopeId,
            needCustomComponentPath
        });
        loaderContext.emitFile(outputPath.replace('.js', '.ssr.js'), ssrResult.code);
    }

    if (options.esModule) {
        const lastIndex = output.lastIndexOf('module.exports = obj;');
        output = output.slice(0, lastIndex) + 'export default obj;'
    }

    this.callback(null, output);

};
